import React, { useEffect, useState } from "react";
import { fetchApi, sendApi } from "../../apis/Index";
import { toast } from "react-hot-toast";

const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    code: "",
    discount: "",
    expiry_date: ""
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const result = await fetchApi(`https://wittynailtip.com/backend/admin-coupons.php?t=${new Date().getTime()}`);
      if (result.data && result.data.success) {
        setCoupons(result.data.data || []);
      } else {
        setCoupons([]);
      }
    } catch (error) {
      console.log("Error fetching coupons", error);
      toast.error("Failed to load coupons");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    if (!formData.code || !formData.discount || !formData.expiry_date) {
      return toast.error("Please fill all fields");
    }
    
    setSubmitting(true);
    try {
      const result = await sendApi({
        action: 'create',
        ...formData
      }, "https://wittynailtip.com/backend/admin-coupons.php");

      if (result.success && result.data && result.data.success) {
        toast.success("Coupon created successfully!");
        setShowModal(false);
        setFormData({ code: "", discount: "", expiry_date: "" });
        fetchCoupons();
      } else {
        toast.error(result.data?.message || "Failed to create coupon");
      }
    } catch (error) {
      toast.error("An error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this coupon?")) return;
    
    try {
      const result = await sendApi({
        action: 'delete',
        id: id
      }, "https://wittynailtip.com/backend/admin-coupons.php");

      if (result.success && result.data && result.data.success) {
        toast.success("Coupon deleted!");
        fetchCoupons();
      } else {
        toast.error(result.data?.message || "Failed to delete coupon");
      }
    } catch (error) {
      toast.error("An error occurred");
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm("WARNING: Are you absolutely sure you want to delete ALL coupons? This cannot be undone!")) return;
    
    try {
      const result = await sendApi({
        action: 'delete_all'
      }, "https://wittynailtip.com/backend/admin-coupons.php");

      if (result.success && result.data && result.data.success) {
        toast.success("All coupons deleted!");
        fetchCoupons();
      } else {
        toast.error(result.data?.message || "Failed to delete all coupons");
      }
    } catch (error) {
      toast.error("An error occurred");
    }
  };

  return (
    <div className="space-y-6 font-jost">
      <div className="flex flex-col md:flex-row justify-between md:items-end gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Coupons &amp; Discounts</h1>
          <p className="text-gray-500 mt-1">Manage promotional codes for customers.</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={handleDeleteAll}
            className="bg-red-50 hover:bg-red-100 text-red-600 px-6 py-2.5 rounded-full font-semibold transition-colors shadow-sm text-sm"
          >
            Delete All
          </button>
          <button 
            onClick={() => setShowModal(true)}
            className="bg-gray-900 hover:bg-black text-white px-6 py-2.5 rounded-full font-semibold transition-colors shadow-sm flex items-center gap-2 text-sm"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path></svg>
            Create Coupon
          </button>
        </div>
      </div>

      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FFB7CF]"></div>
            </div>
          ) : (
            <table className="w-full text-left text-sm text-gray-500">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50/50">
                <tr>
                  <th scope="col" className="px-6 py-4 rounded-tl-3xl">Code</th>
                  <th scope="col" className="px-6 py-4">Discount</th>
                  <th scope="col" className="px-6 py-4">Expiry Date</th>
                  <th scope="col" className="px-6 py-4">Status</th>
                  <th scope="col" className="px-6 py-4 text-right rounded-tr-3xl">Action</th>
                </tr>
              </thead>
              <tbody>
                {coupons.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-10 text-center text-gray-500">
                      <div className="flex flex-col items-center justify-center">
                        <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"></path></svg>
                        <p>No active coupons found.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  coupons.map((coupon) => {
                    const isExpired = new Date(coupon.expiry_date) < new Date();
                    return (
                      <tr key={coupon.id} className="bg-white border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                        <td className="px-6 py-4 font-bold text-gray-900 tracking-wider">
                          {coupon.code}
                        </td>
                        <td className="px-6 py-4 font-medium text-gray-900">
                          {coupon.discount}%
                        </td>
                        <td className="px-6 py-4">
                          {coupon.expiry_date}
                        </td>
                        <td className="px-6 py-4">
                          {isExpired ? (
                            <span className="bg-red-50 text-red-600 px-3 py-1 rounded-full text-xs font-semibold">Expired</span>
                          ) : (
                            <span className="bg-[#E1F7EB] text-[#20AA5F] px-3 py-1 rounded-full text-xs font-semibold">Active</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button 
                            onClick={() => handleDelete(coupon.id)}
                            className="text-gray-400 hover:text-red-500 transition-colors p-2"
                            title="Delete Coupon"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                          </button>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Create Coupon Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Create New Coupon</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>
            
            <form onSubmit={handleCreateCoupon} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Coupon Code</label>
                <input 
                  type="text"
                  required
                  placeholder="e.g. SUMMER2024"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#FFB7CF] focus:border-transparent outline-none transition-all uppercase"
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Discount Amount (%)</label>
                <input 
                  type="number"
                  required
                  min="1"
                  max="100"
                  placeholder="e.g. 15"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#FFB7CF] focus:border-transparent outline-none transition-all"
                  value={formData.discount}
                  onChange={(e) => setFormData({...formData, discount: e.target.value})}
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Expiry Date</label>
                <input 
                  type="date"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#FFB7CF] focus:border-transparent outline-none transition-all"
                  value={formData.expiry_date}
                  onChange={(e) => setFormData({...formData, expiry_date: e.target.value})}
                />
              </div>
              
              <div className="pt-4 flex gap-3">
                <button 
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="w-1/2 px-4 py-3 rounded-full border border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={submitting}
                  className="w-1/2 px-4 py-3 rounded-full bg-[#FFB7CF] text-white font-semibold hover:bg-[#ff9cbb] transition-colors disabled:opacity-70 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  ) : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCoupons;
